/**
 * RecentPlayers — the game page's "Recently played by" rail: the last few
 * people who actually played THIS game, freshest first.
 *
 * The counterpart to the home page's PresenceRail. That one answers "who is
 * around right now"; this one answers "does anyone play this game", which is
 * the question a visitor deciding whether to press Start is really asking — and
 * it is the one thing a play counter cannot tell them, because a number has no
 * faces on it.
 *
 * Everything that decides WHO is listed lives in the `public_game_recent_players`
 * view (0047, amended by 0048): capped at 5, public+live games only, real
 * claimed players only — EVERYONE who played, the game's creator and editors
 * included, because a play is a play and a rail that drops the person you just
 * watched play would be lying by omission. This component renders what comes
 * back and nothing more.
 *
 * One-shot fetch, no subscription: a list of who played EARLIER has nothing to
 * gain from streaming (the presence rail owns "right now"), and the game page
 * already carries several live subscriptions. An empty list renders NOTHING —
 * an unplayed game should read as new, not as abandoned.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useServices } from '../services/context.js';
import type { GameRecentPlayer } from '../services/types.js';
import { UserAvatar } from './UserAvatar.js';
import { formatTimeAgo } from './relativeTime.js';

export function RecentPlayers({ gameKey }: { gameKey: string }) {
  const services = useServices();
  const [players, setPlayers] = useState<GameRecentPlayer[] | null>(null);

  useEffect(() => {
    let active = true;
    setPlayers(null); // component instances are reused across game→game navigation
    services
      .gameRecentPlayers(gameKey)
      .then((rows) => {
        if (active) setPlayers(rows);
      })
      .catch(() => {
        // The rail is decoration on a page that stands without it; a failed
        // read renders nothing rather than an error the visitor can't act on.
        if (active) setPlayers([]);
      });
    return () => {
      active = false;
    };
  }, [services, gameKey]);

  if (!players || players.length === 0) return null;

  return (
    <div className="panel" data-testid="recent-players">
      <h2 className="panel__title">Recently played by</h2>
      <ul className="recent-players">
        {players.map((p) => (
          <li key={p.uid} className="recent-players__item" data-testid="recent-player">
            <UserAvatar avatarKey={p.avatarKey} size={30} alt={`@${p.handle}`} />
            <Link className="recent-players__handle" to={`/u/${p.handle}`}>
              @{p.handle}
            </Link>
            <span
              className="recent-players__when muted"
              title={new Date(p.lastPlayedAt).toLocaleString()}
            >
              {formatTimeAgo(p.lastPlayedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
